import React, { useState } from 'react';
import Layout from '@layout';
import { LocationProps } from '@models/navigation';
import { PageContextData, SlugPrefix } from '@models/pages';
import {
  formatDateYear,
  formatSeoDescription,
  ShowEventSections,
  slugifyString,
} from '@services';
import About from './About';
import AllVideos from './AllVideos';
import Hero from './Hero';
import Home from './Home';
import Navigation from './Navigation';
import VideoSwiper from './VideoSwiper';
import SEO from '../SEO';

type Props = {
  pageContext: PageContextData,
  data: any,
  location: LocationProps,
  show: Boolean,
}
function ShowOrEventPage({
  pageContext, data,
  location, show = true,
}:Props) {
  const { globalCmsButtons, lang, stringTranslations } = pageContext;
  const [pageSection, setPageSection] = useState(
    show ? ShowEventSections.ShowHome : ShowEventSections.EventHome,
  );
  const pageLocales: { lang: string, slug: string }[] = data.pageLocales.nodes
    .map((pageLocale: any) => {
      const pageSlug = show ? `${SlugPrefix.ChdTvShows}${pageLocale.slug}` : `${SlugPrefix.ChdTvEvents}${pageLocale.slug}`;
      return ({
        lang: pageLocale.node_locale,
        slug: `${pageLocale.node_locale !== 'en' ? `/${pageLocale.node_locale}` : ''}${pageSlug}`,
      }
      );
    });

  // TODO: Delete this pattern (param reassign) across the project after refactoring header
  // eslint-disable-next-line no-param-reassign
  pageContext.locales = pageLocales;

  const { showOrEventData } = data;
  const hosts = showOrEventData?.showHosts || showOrEventData?.eventHosts || [];
  const videos = showOrEventData?.episodes || showOrEventData?.eventVideos || [];

  const mappedVideos = videos
    ?.filter((video: any) => video.visible)
    .map((video: any) => {
      const videoSlug = show ? `${SlugPrefix.ChdTvShows}${showOrEventData.slug}/${video.slug}/` : `${SlugPrefix.ChdTvEvents}${showOrEventData.slug}/${video.slug}`;

      return ({
        ...video,
        slug: video.node_locale === 'en' ? `${videoSlug}` : `/${video.node_locale}${videoSlug}/`,
        showYear: video.publishDate
          ? formatDateYear(video.publishDate)
          : new Date().getFullYear(),
      });
    });

  const navigationTopics = showOrEventData.topics
    ? showOrEventData.topics.reduce((acc :any, cv :any) => {
      if (mappedVideos.some((video:any) => video.tags?.some((tag : any) => tag.name === cv.name))) {
        acc.push(cv.name);
        return acc;
      }
      return acc;
    }, []) : [];

  const pageNavigation: string[] = [
    show ? ShowEventSections.ShowHome : ShowEventSections.EventHome,
    ShowEventSections.About,
    ...navigationTopics,
  ];

  if (mappedVideos.length) {
    pageNavigation.push(ShowEventSections.All);
  }

  return (
    <Layout
      location={location}
      lang={lang}
      locales={pageLocales}
      page={pageContext}
    >
      <SEO
        title={showOrEventData.title}
        image={showOrEventData.thumbnailMediaStandard?.url}
        lang={lang}
        locales={pageLocales}
        description={formatSeoDescription(showOrEventData.description?.childMarkdownRemark?.html)}
      />
      <Hero heroData={showOrEventData.heroImage} title={showOrEventData.title} />
      <section className="mx-auto text-white max-w-[1300px] lg:mt-[25px] mt-[20px] lg:mb-[40px] mb-[30px] px-5">
        <Navigation
          navigationMenu={pageNavigation}
          pageSection={pageSection}
          updateSection={setPageSection}
        />
        {(() => {
          switch (pageSection) {
            case show ? ShowEventSections.ShowHome : ShowEventSections.EventHome:
              return (
                <Home
                  lang={lang}
                  globalCmsButtons={globalCmsButtons}
                  hosts={hosts}
                  showOrEventData={showOrEventData}
                  stringTranslations={stringTranslations}
                  videos={mappedVideos}
                />
              );
            case ShowEventSections.About:
              return (
                <About
                  description={showOrEventData.description?.childMarkdownRemark?.html || ''}
                  hosts={hosts}
                  lang={lang}
                  stringTranslations={stringTranslations}
                />
              );
            case ShowEventSections.All:
              if (mappedVideos) {
                return (
                  <AllVideos
                    lang={lang}
                    stringTranslations={stringTranslations}
                    videos={mappedVideos}
                  />
                );
              }
              break;
            default:
              return (
                <VideoSwiper
                  key={slugifyString(pageSection)}
                  id={slugifyString(pageSection)}
                  showDate
                  title={pageSection}
                  videos={mappedVideos
                    .filter((e:any) => e.tags?.some((tag:any) => tag.name === pageSection))}
                />
              );
          }
        })()}
      </section>
    </Layout>
  );
}

export default ShowOrEventPage;
